/**
 * Sentry initialization helpers for the Vue 3 admin SPA.
 *
 * Activation: a non-empty `dsn`. Without a DSN every function is a safe no-op
 * and zero Sentry SDK code runs.
 */
import * as Sentry from '@sentry/vue';
import { isValidDsn } from './types.js';
let enabled = false;
/**
 * Initialize Sentry for the Vue admin app.
 *
 * Wire from main.ts AFTER the router is created and BEFORE app.mount():
 *
 *   import { initAdminSentry } from '@gencay-akademi/sentry-config/admin'
 *   initAdminSentry({ app, router, config: {
 *     dsn: import.meta.env.VITE_SENTRY_DSN,
 *     environment: import.meta.env.MODE ?? 'production',
 *     release: import.meta.env.VITE_APP_VERSION,
 *     tracesSampleRate: 0.1,
 *   }})
 */
export function initAdminSentry({ app, router, config }) {
    if (!isValidDsn(config.dsn)) {
        enabled = false;
        return;
    }
    enabled = true;
    Sentry.init({
        app,
        dsn: config.dsn,
        environment: config.environment,
        release: config.release ?? 'unknown',
        integrations: [
            Sentry.browserTracingIntegration({ router }),
            Sentry.replayIntegration({
                maskAllText: true,
                blockAllMedia: true,
            }),
        ],
        tracesSampleRate: config.tracesSampleRate ?? 0.1,
        replaysSessionSampleRate: config.replaysSessionSampleRate ?? 0,
        replaysOnErrorSampleRate: config.replaysOnErrorSampleRate ?? 1.0,
        sendDefaultPii: config.sendDefaultPii ?? false,
    });
}
/** Set the user context (call after login). */
export function setAdminSentryUser(userId, email, username) {
    if (!enabled)
        return;
    Sentry.setUser({ id: userId, email, username });
}
/** Clear user context (call on logout). */
export function clearAdminSentryUser() {
    if (!enabled)
        return;
    Sentry.setUser(null);
}
/** @internal — test-only reset. */
export function __resetForTests() {
    enabled = false;
}
